import { InteropStore, Destination, ConnectDestination, InteropBroadcastInternal } from "./types";
import { filterForClientAuthorization } from "./modules/authorize";
import { isEqual } from "lodash";
import Logger from "../../clients/logger";

const deepIndexOf = (arr: any[], item: any): number => {
	for (var i = 0; i < arr.length; i++) {
		const arrayItem = arr[i];
		if (isEqual(arrayItem, item)) return i;
	}
	return -1;
};

const deDupe = (arr: any[]): any[] => arr.filter((item, index) => deepIndexOf(arr, item) === index);

type TriggeredRule = InteropStore["root"]["testing"]["triggeredRules"][0];

const triggeredRule = (
	state: InteropStore,
	rulePhase: string,
	result: string,
	rule: any,
	destination: Destination
): TriggeredRule => {
	return {
		rulePhase,
		result,
		uuid: destination.uuid,
		rule,
		destination,
	};
};

const processBroadcastersRules = (
	state: InteropStore,
	fsa: InteropBroadcastInternal,
	destinations: Destination[]
): [Destination[], TriggeredRule[]] => {
	const { uuid } = fsa.meta;

	const rules = state.resolver.clients[uuid]?.config?.interop?.selectConnect ?? [];
	let triggeredRules: TriggeredRule[] = [];

	// Ensure that there is at least one destination. Without any destinations then generic rules that apply
	// to the broadcaster would never run. This is a bit of a hack but can't find a more elegant approach.
	destinations = destinations.length
		? destinations
		: [
				{
					type: "client",
					routerChannel: "",
					uuid: fsa.meta.uuid,
					context: fsa.payload.context,
					contextType: fsa.payload.context?.type,
				},
		  ];

	/**
	 * Loop through all the destination messages. Apply rules for the *broadcaster* against each destination.
	 * Any given destination may be left intact, or replaced if a "fwd" rule is triggered.
	 * The destinations may also expand if a "cc" rule is triggered.
	 *
	 * The resulting array of destinations may end up including dupes. These are removed by running a dedupe routine.
	 */
	const connectedDestinations = deDupe(
		destinations.flatMap((destination) => {
			let connectedDestination: Destination | null = destination;

			let ccDestinations: Destination[] = [];
			rules.forEach((rule) => {
				const { contextType, cc, fwd, to, from, authorize } = rule;
				// Proceed only if the rule's contextType matches the context we're broadcasting, or if the rule applies to any context
				if (contextType && contextType !== fsa.payload.context?.type) return;
				// Proceed only if the rule targets the destination app/channel or targets all destinations
				//if(to && to!==destination.appName) return;
				// From rules can never apply to the broadcasting app
				if (from) return;

				// cc and fwd current only support selectConnect destinations. In the future we might want to selectConnect directly to apps or channels.
				if (cc) {
					const ccDestination: ConnectDestination = {
						type: "selectConnect",
						name: cc,
						uuid,
						contextType: destination.contextType,
						context: destination.context,
					};
					ccDestinations.push(ccDestination);
					triggeredRules.push(triggeredRule(state, "broadcast", "cc", rule, ccDestination));
				}
				if (fwd) {
					connectedDestination = {
						type: "selectConnect",
						name: fwd,
						uuid,
						contextType: destination.contextType,
						context: destination.context,
					};
					triggeredRules.push(triggeredRule(state, "broadcast", "fwd", rule, connectedDestination));
				}
			});
			return (connectedDestination ? [connectedDestination] : []).concat(ccDestinations);
		})
	);

	// Return the connected destinations, but eliminate the default broadcast destination if it still exists
	const finalDestinations = connectedDestinations.filter(
		(destination) => destination.type !== "client" || destination.routerChannel !== ""
	);
	Logger.debug("interopService.selectConnect.processBroadcastersRules", {
		finalDestinations,
		triggeredRules,
		connectedDestinations,
		rules,
	});
	return [finalDestinations, triggeredRules];
};

const processDestinationsRules = (
	state: InteropStore,
	fsa: InteropBroadcastInternal,
	destinations: Destination[]
): [Destination[], TriggeredRule[]] => {
	let triggeredRules: TriggeredRule[] = [];

	const connectedDestinations = deDupe(
		destinations.flatMap((destination) => {
			const name = destination.type === "client" ? destination.uuid : destination.name;
			const rules = state.resolver.clients[name]?.config?.interop?.selectConnect ?? [];

			let primaryDestination: Destination | null = destination;

			let ccDestinations: Destination[] = [];
			rules.forEach((rule) => {
				const { contextType, cc, fwd, to, from, authorize } = rule;
				// Proceed only if the rule's contextType matches the destination's listener, or the destination is listening for any context type, or if the rule applies to any context type
				if (contextType && contextType !== destination.contextType && destination.contextType !== "*") return;
				// Proceed only if the rule targets the broadcasting app/channel or targets any broadcast
				//if(from && from!==destination.appName) return;
				// "to" rules don't apply for destinations
				if (to) return;

				// cc and fwd current only support selectConnect destinations. In the future we might want to connect directly to apps or channels.
				if (cc) {
					const ccDestination: ConnectDestination = {
						type: "selectConnect",
						name: cc,
						uuid: destination.uuid,
						contextType,
						context: fsa.payload.context,
					};
					ccDestinations.push(ccDestination);
					triggeredRules.push(triggeredRule(state, "destination", "cc", rule, ccDestination));
				}
				if (fwd) {
					primaryDestination = {
						type: "selectConnect",
						name: fwd,
						uuid: destination.uuid,
						contextType,
						context: fsa.payload.context,
					};
					triggeredRules.push(triggeredRule(state, "destination", "fwd", rule, primaryDestination));
				}
			});
			return (primaryDestination ? [primaryDestination] : []).concat(ccDestinations);
		})
	);

	return [connectedDestinations, triggeredRules];
};

const processAuthorizationRules = (
	state: InteropStore,
	fsa: InteropBroadcastInternal,
	destinations: Destination[]
): Destination[] => {
	const authorizedDesinationUuidList = filterForClientAuthorization({
		state,
		contextType: fsa.payload.context?.type || "",
		destinationClientUuidList: destinations.map((dest) => dest.uuid),
		originClientUuid: fsa.meta.uuid,
	});

	return destinations.filter((dest) => authorizedDesinationUuidList?.includes(dest.uuid));
};

export const selectConnect = (
	state: InteropStore,
	fsa: InteropBroadcastInternal,
	destinations: Destination[]
): [Destination[], TriggeredRule[]] => {
	let triggeredRulesBroadcast: TriggeredRule[] = [];
	let triggeredRulesDestination: TriggeredRule[] = [];
	[destinations, triggeredRulesBroadcast] = processBroadcastersRules(state, fsa, destinations);
	[destinations, triggeredRulesDestination] = processDestinationsRules(state, fsa, destinations);
	destinations = processAuthorizationRules(state, fsa, destinations);

	const finalTriggeredRules = triggeredRulesBroadcast.concat(triggeredRulesDestination);
	return [destinations, finalTriggeredRules];
};
